import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  Divider,
  Grid,
  FormGroup,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Chip
} from '@mui/material';
import { ExpandMore, ExpandLess, Add } from '@mui/icons-material';
import { TopNavBar } from 'components/top_nav_bar';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useCuffaStore } from 'stores/cuffa/cuffa.store';
import { ROUTES } from 'routes/constants';
import { NumericFormat } from 'react-number-format';
import { CuffaAPI } from 'api/cuffa/cuffa.api';
import { debounce } from 'lodash';

export enum CuffaMeasurementUnitEnum {
  QUILOGRAMAS = 'QUILOGRAMAS',
  TONELADAS = 'TONELADAS',
  LITROS = 'LITROS',
  UNIDADES = 'UNIDADES',
  CABECAS = 'CABEÇAS',
  MIL_FRUTOS = 'MIL FRUTOS',
  ARROBAS = 'ARROBAS',
  MACOS = 'MAÇOS',
  FARDOS = 'FARDOS',
  CAIXAS = 'CAIXAS',
  MIL_LITROS = 'MIL LITROS',
}

// Tipos e constantes
type ProdutoProcessado = {
  id: string;
  nome: string;
  origem: 'animal' | 'vegetal';
  selecionado: boolean;
  expandido: boolean;
  volumeProduzido: number | null;
  unidadeVolume: string;
  volumeComercializado: number | null;
  valorComercializado: number | null;
  volumeConsumido: number | null;
  volumePerdido: number | null;
  mesesDisponibilidade: {
    anoTodo: boolean;
    meses: string[];
  };
  isCustom?: boolean;
};

interface ProdutoApi {
  key: string;
  value: string;
}

interface UnidadeMedidaApi {
  key: string;
  value: string;
}

interface ProdutoSalvo extends Omit<ProdutoProcessado, 'mesesDisponibilidade'> {
  mesesDisponibilidade?: {
    anoTodo?: boolean;
    meses?: string[];
  };
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MES_MAP_PAYLOAD: { [key: string]: string } = {
  jan: 'january', fev: 'february', mar: 'march', abr: 'april', mai: 'may', jun: 'june',
  jul: 'july', ago: 'august', set: 'september', out: 'october', nov: 'november', dez: 'december'
};

// Mapeamento reverso para carregar os dados
const MES_MAP_REVERSE: { [key: string]: string } = {
  january: 'jan', february: 'fev', march: 'mar', april: 'abr', may: 'mai', june: 'jun',
  july: 'jul', august: 'ago', september: 'set', october: 'out', november: 'nov', december: 'dez'
};

export function criarProdutoPadrao(produto: ProdutoApi, origem: 'animal' | 'vegetal'): ProdutoProcessado {
  return {
    id: produto.key,
    nome: produto.value,
    origem,
    selecionado: false,
    expandido: false,
    volumeProduzido: null,
    unidadeVolume: '',
    volumeComercializado: null,
    valorComercializado: null,
    volumeConsumido: null,
    volumePerdido: null,
    mesesDisponibilidade: {
      anoTodo: false,
      meses: []
    }
  };
}

export function normalizarProduto(produto: ProdutoSalvo): ProdutoProcessado {
  return {
    id: produto.id,
    nome: produto.nome,
    origem: produto.origem,
    selecionado: produto.selecionado ?? false,
    expandido: produto.expandido ?? false,
    volumeProduzido: produto.volumeProduzido ?? null,
    unidadeVolume: produto.unidadeVolume ?? '',
    volumeComercializado: produto.volumeComercializado ?? null,
    valorComercializado: produto.valorComercializado ?? null,
    volumeConsumido: produto.volumeConsumido ?? null,
    volumePerdido: produto.volumePerdido ?? null,
    mesesDisponibilidade: {
      anoTodo: produto.mesesDisponibilidade?.anoTodo ?? false,
      meses: produto.mesesDisponibilidade?.meses ?? []
    },
    isCustom: produto.isCustom ?? false
  };
}

export function mapApiProduct(p: any): ProdutoProcessado {
  let availabilityMonths = p.available_months;
  if (typeof availabilityMonths === 'string') {
    try {
      availabilityMonths = JSON.parse(availabilityMonths);
    } catch (e) {
      availabilityMonths = {};
    }
  }

  const mesesSelecionados: string[] = [];
  if (availabilityMonths && !availabilityMonths.available_year_round) {
    for (const [key, value] of Object.entries(availabilityMonths)) {
      if (value === true && MES_MAP_REVERSE[key]) {
        mesesSelecionados.push(MES_MAP_REVERSE[key]);
      }
    }
  }

  return {
    id: p.uuid,
    nome: p.custom_product_name || p.product_id,
    origem: (p.production_type || '').toLowerCase() as 'animal' | 'vegetal',
    selecionado: true,
    expandido: false,
    volumeProduzido: parseFloat(p.volume_produced) || null,
    unidadeVolume: p.measurement_unit || '',
    volumeComercializado: parseFloat(p.volume_commercialized) || null,
    valorComercializado: parseFloat(p.commercialized_value) || null,
    volumeConsumido: parseFloat(p.volume_consumed_donated) || null,
    volumePerdido: parseFloat(p.volume_lost) || null,
    mesesDisponibilidade: {
      anoTodo: availabilityMonths?.available_year_round || false,
      meses: mesesSelecionados,
    },
    isCustom: !p.product_id
  };
}

export function mergeProdutosBaseWithSaved(todosProdutosBase: ProdutoProcessado[], produtosSalvosNormalizados: ProdutoProcessado[]) {
  const produtosAtualizados = todosProdutosBase.map(pBase => {
    const salvo = produtosSalvosNormalizados.find((pSalvo) => pSalvo.id === pBase.id && !pSalvo.isCustom);
    return salvo ? { ...pBase, ...salvo } : pBase;
  });

  const produtosCustomSalvos = produtosSalvosNormalizados.filter((p) => p.isCustom);
  return [...produtosAtualizados, ...produtosCustomSalvos];
}

export function buildProcessedProductsPayload(produtos: ProdutoProcessado[], possuiProcessados: string) {
  return {
    family_produces_processed_products: possuiProcessados === 'sim',
    processed_products: possuiProcessados === 'sim' ? produtos.filter(p => p.selecionado).map(p => {
      const volumeCommercialized = p.volumeComercializado && p.volumeComercializado >= 0
        ? p.volumeComercializado
        : null;

      const availability_months: { [key: string]: boolean } = {
        available_year_round: p.mesesDisponibilidade.anoTodo
      };
      Object.values(MES_MAP_PAYLOAD).forEach(monthName => {
        availability_months[monthName] = false;
      });

      if (!p.mesesDisponibilidade.anoTodo) {
        p.mesesDisponibilidade.meses.forEach(mes => {
          const monthKey = MES_MAP_PAYLOAD[mes];
          if (monthKey) {
            availability_months[monthKey] = true;
          }
        });
      }

      return {
        product_id: p.isCustom ? p.nome : p.id,
        origin: p.origem.toUpperCase(),
        volume_produced: p.volumeProduzido,
        volume_produced_unit: p.unidadeVolume,
        volume_commercialized: volumeCommercialized,
        volume_commercialized_unit: p.unidadeVolume,
        average_commercialized_value: p.valorComercializado,
        volume_self_consumption: p.volumeConsumido,
        volume_self_consumption_unit: p.unidadeVolume,
        volume_lost: p.volumePerdido,
        volume_lost_unit: p.unidadeVolume,
        availability_months
      };
    }) : []
  };
}

export function validateProdutos(possuiProcessados: string, produtos: ProdutoProcessado[]): { valid: boolean; message?: string } {
  if (!possuiProcessados) {
    return { valid: false, message: 'Informe se a unidade familiar produz itens processados.' };
  }
  if (possuiProcessados === 'sim') {
    const produtosSelecionados = produtos.filter(p => p.selecionado);
    if (produtosSelecionados.length === 0) {
      return { valid: false, message: 'Selecione pelo menos um produto processado.' };
    }

    for (const produto of produtosSelecionados) {
      if (!produto.volumeProduzido || produto.volumeProduzido <= 0) {
        return { valid: false, message: `Informe o volume produzido válido para ${produto.nome}.` };
      }
      if (!produto.unidadeVolume) {
        return { valid: false, message: `Selecione a unidade de medida para ${produto.nome}.` };
      }
    }
  }
  return { valid: true };
}

// Componente memoizado para os campos do produto
const CamposProduto = React.memo(({ produto, onChange, unidadesMedida }: {
  produto: ProdutoProcessado;
  onChange: (campo: string, valor: any) => void;
  unidadesMedida: UnidadeMedidaApi[];
}) => {
  return (
    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume produzido no último ano"
            fullWidth
            value={produto.volumeProduzido ?? ''}
            onValueChange={(values) => onChange('volumeProduzido', values.floatValue)}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Unidade de medida</InputLabel>
            <Select
              value={produto.unidadeVolume}
              onChange={(e) => onChange('unidadeVolume', e.target.value)}
              label="Unidade de medida"
            >
              {Object.values(CuffaMeasurementUnitEnum).map(unidade => (
                <MenuItem key={unidade} value={unidade}>
                  {unidade}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume comercializado no último ano"
            fullWidth
            value={produto.volumeComercializado ?? ''}
            onValueChange={(values) => onChange('volumeComercializado', values.floatValue)}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Valor comercializado no último ano (R$)"
            fullWidth
            value={produto.valorComercializado ?? ''}
            onValueChange={(values) => onChange('valorComercializado', values.floatValue)}
            decimalScale={2}
            allowNegative={false}
            thousandSeparator="."
            decimalSeparator=","
            prefix="R$ "
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume consumido/doado/trocado"
            fullWidth
            value={produto.volumeConsumido ?? ''}
            onValueChange={(values) => onChange('volumeConsumido', values.floatValue)}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <NumericFormat
            customInput={TextField}
            label="Volume perdido no último ano"
            fullWidth
            value={produto.volumePerdido ?? ''}
            onValueChange={(values) => onChange('volumePerdido', values.floatValue)}
            decimalScale={2}
            allowNegative={false}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">
              Meses de disponibilidade para comercialização: *
            </FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={produto.mesesDisponibilidade.anoTodo}
                    onChange={(e) => onChange('mesesDisponibilidade.anoTodo', e.target.checked)}
                  />
                }
                label="Disponível o ano todo"
              />
              {!produto.mesesDisponibilidade.anoTodo && (
                <>
                  {MESES.map((mes) => (
                    <FormControlLabel
                      key={mes}
                      control={
                        <Checkbox
                          checked={produto.mesesDisponibilidade.meses.includes(mes)}
                          onChange={(e) => {
                            const novosMeses = e.target.checked
                              ? [...produto.mesesDisponibilidade.meses, mes]
                              : produto.mesesDisponibilidade.meses.filter(m => m !== mes);
                            onChange('mesesDisponibilidade.meses', novosMeses);
                          }}
                        />
                      }
                      label={mes.charAt(0).toUpperCase() + mes.slice(1)}
                    />
                  ))}
                </>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
});

CamposProduto.displayName = 'CamposProduto';

// Componente memoizado para produtos normais
const ProdutoItem = React.memo(({ produto, onSelect, onExpand, onChange, unidadesMedida }: {
  produto: ProdutoProcessado;
  onSelect: (id: string) => void;
  onExpand: (id: string) => void;
  onChange: (id: string, campo: string, valor: any) => void;
  unidadesMedida: UnidadeMedidaApi[];
}) => {
  return (
    <Box mb={1}>
      <Box
        display="flex"
        alignItems="center"
        sx={{
          p: 1,
          backgroundColor: produto.selecionado ? '#f5f5f5' : 'transparent',
          cursor: 'pointer',
          border: '1px solid #ddd',
          borderRadius: 1
        }}
      >
        <Checkbox
          checked={produto.selecionado}
          onChange={() => onSelect(produto.id)}
          size="small"
        />
        <Typography
          flexGrow={1}
          onClick={() => onSelect(produto.id)}
          variant="body2"
        >
          {produto.nome}
        </Typography>
        {produto.selecionado && (
          <IconButton
            onClick={() => onExpand(produto.id)}
            size="small"
          >
            {produto.expandido ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>

      <Collapse in={produto.selecionado && produto.expandido} unmountOnExit>
        <CamposProduto
          produto={produto}
          onChange={(campo, valor) => onChange(produto.id, campo, valor)}
          unidadesMedida={unidadesMedida}
        />
      </Collapse>
    </Box>
  );
});

ProdutoItem.displayName = 'ProdutoItem';

// Componente memoizado para produtos customizados
const CustomProdutoItem = React.memo(({ produto, onExpand, onChange, onRemove, unidadesMedida }: {
  produto: ProdutoProcessado;
  onExpand: (id: string) => void;
  onChange: (id: string, campo: string, valor: any) => void;
  onRemove: (id: string) => void;
  unidadesMedida: UnidadeMedidaApi[];
}) => {
  return (
    <Box mb={1}>
      <Box display="flex" alignItems="center">
        <Chip
          label={produto.nome}
          onDelete={() => onRemove(produto.id)}
          color="primary"
          sx={{ mr: 2, flexGrow: 1, justifyContent: 'space-between' }}
        />
        <IconButton
          onClick={() => onExpand(produto.id)}
          size="small"
        >
          {produto.expandido ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={produto.expandido}>
        <CamposProduto
          produto={produto}
          onChange={(campo, valor) => onChange(produto.id, campo, valor)}
          unidadesMedida={unidadesMedida}
        />
      </Collapse>
    </Box>
  );
});

CustomProdutoItem.displayName = 'CustomProdutoItem';

export default function ProdutosProcessados() {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { salvarDadosParciais, dadosParciais } = useCuffaStore();

  const [possuiProcessados, setPossuiProcessados] = useState<string>('');
  const [produtos, setProdutos] = useState<ProdutoProcessado[]>([]);
  const [unidadesMedida, setUnidadesMedida] = useState<UnidadeMedidaApi[]>([]);
  const [novoProdutoCustom, setNovoProdutoCustom] = useState('');
  const [origemCustom, setOrigemCustom] = useState<'animal' | 'vegetal'>('animal');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [loading, setLoading] = useState(true);

  // using exported helper functions: criarProdutoPadrao, normalizarProduto

  const debouncedHandleProdutoChange = useMemo(
    () => debounce((id: string, campo: string, valor: any) => {
      setProdutos(prev => prev.map(p => {
        if (p.id !== id) return p;

        if (campo.startsWith('mesesDisponibilidade.')) {
          const subCampo = campo.split('.')[1];
          return {
            ...p,
            mesesDisponibilidade: {
              ...p.mesesDisponibilidade,
              [subCampo]: valor
            }
          };
        }

        return { ...p, [campo]: valor };
      }));
    }, 300),
    []
  );

  // Função para carregar dados da API
  const carregarDadosDaAPI = useCallback(async () => {
    if (!uuid) return;

    try {
      const response = await CuffaAPI.getDataByID(uuid);

      if (response && response.dados) {
        const apiData = response.dados;

        // Definir se produz processados
        setPossuiProcessados(apiData.family_produces_processed_products ? 'sim' : 'nao');

        // Carregar produtos da API
        if (apiData.family_produces_processed_products && apiData.products) {
          const produtosDaAPI = apiData.products
            .filter((p: any) => p.management_type === null) // Filtra produtos processados
            .map((p: any) => mapApiProduct(p));

          setProdutos(prev => {
            // Manter produtos não selecionados da lista base
            const produtosBase = prev.filter(p => !p.selecionado && !p.isCustom);
            return [...produtosBase, ...produtosDaAPI];
          });
        }
      }
    } catch (error) {
      console.error("Erro ao carregar dados da API:", error);
      setSnackbar({ open: true, message: 'Erro ao carregar dados salvos.', severity: 'error' });
    }
  }, [uuid]);

  useEffect(() => {
    const carregarDadosIniciais = async () => {
      try {
        setLoading(true);

        // Carregar produtos disponíveis
        const response = await CuffaAPI.getAvailableProcessedProducts();
        if (response) {
          setUnidadesMedida(response.measurement_units || []);

          const produtosAnimaisApi = (response.animal_products || []).map((p: ProdutoApi) => criarProdutoPadrao(p, 'animal'));
          const produtosVegetaisApi = (response.vegetal_products || []).map((p: ProdutoApi) => criarProdutoPadrao(p, 'vegetal'));
          const todosProdutosBase = [...produtosAnimaisApi, ...produtosVegetaisApi];

          // Verificar se há dados salvos localmente
          const dadosSalvos = dadosParciais.produtosProcessados;
          if (dadosSalvos?.produtos?.length) {
            setPossuiProcessados(dadosSalvos.possui || 'nao');
            const produtosSalvosNormalizados = dadosSalvos.produtos.map(normalizarProduto);

            const produtosAtualizados = todosProdutosBase.map(pBase => {
              const salvo = produtosSalvosNormalizados.find((pSalvo: { id: any; isCustom: any; }) => pSalvo.id === pBase.id && !pSalvo.isCustom);
              return salvo ? { ...pBase, ...salvo } : pBase;
            });

            const produtosCustomSalvos = produtosSalvosNormalizados.filter((p: { isCustom: any; }) => p.isCustom);
            setProdutos([...produtosAtualizados, ...produtosCustomSalvos]);
          } else {
            // Se não há dados locais, carregar da API
            setProdutos(todosProdutosBase);
            await carregarDadosDaAPI();
          }
        }
      } catch (error) {
        console.error("Erro ao carregar produtos:", error);
        setSnackbar({ open: true, message: 'Erro ao carregar produtos.', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    carregarDadosIniciais();
  }, [dadosParciais.produtosProcessados, criarProdutoPadrao, normalizarProduto, carregarDadosDaAPI]);

  // ... restante do código permanece igual ...

  const handleSelecionarProduto = useCallback((id: string) => {
    setProdutos(prev => prev.map(p =>
      p.id === id
        ? { ...p, selecionado: !p.selecionado, expandido: !p.selecionado }
        : p
    ));
  }, []);

  const handleExpandirProduto = useCallback((id: string) => {
    setProdutos(prev => prev.map(p =>
      p.id === id ? { ...p, expandido: !p.expandido } : p
    ));
  }, []);

  const handleAdicionarProdutoCustom = useCallback(() => {
    const nome = novoProdutoCustom.trim();
    if (!nome) {
      setSnackbar({ open: true, message: 'Digite um nome para o produto.', severity: 'error' });
      return;
    }
    const novoProduto: ProdutoProcessado = {
      id: `custom-${Date.now()}`,
      nome,
      origem: origemCustom,
      selecionado: true,
      expandido: true,
      isCustom: true,
      volumeProduzido: null,
      unidadeVolume: '',
      volumeComercializado: null,
      valorComercializado: null,
      volumeConsumido: null,
      volumePerdido: null,
      mesesDisponibilidade: {
        anoTodo: false,
        meses: []
      }
    };

    setProdutos(prev => [...prev, novoProduto]);
    setNovoProdutoCustom('');
  }, [novoProdutoCustom, origemCustom]);

  const handleRemoverProdutoCustom = useCallback((id: string) => {
    setProdutos(prev => prev.filter(p => p.id !== id));
  }, []);

  const validate = useCallback(() => {
    const result = validateProdutos(possuiProcessados, produtos);
    if (!result.valid) {
      setSnackbar({ open: true, message: result.message || 'Validação inválida.', severity: 'error' });
    }
    return result.valid;
  }, [possuiProcessados, produtos]);

  const handleSave = useCallback(async (navigateNext = false) => {
    if (!validate()) return;
    if (!uuid) {
      setSnackbar({ open: true, message: 'Erro: UUID da coleta não encontrado.', severity: 'error' });
      return;
    }

    const dataToSaveLocal = {
      possui: possuiProcessados,
      produtos: possuiProcessados === 'sim' ? produtos.filter(p => p.selecionado) : []
    };

    salvarDadosParciais({
      produtosProcessados: dataToSaveLocal,
      ultimaRota: location.pathname,
    });

    const produtosSelecionados = produtos.filter(p => p.selecionado);
    const payload = buildProcessedProductsPayload(produtos, possuiProcessados);

    try {
      await CuffaAPI.updateProcessedProducts(uuid, payload);
      setSnackbar({ open: true, message: 'Dados salvos com sucesso!', severity: 'success' });

      if (navigateNext) {
        setTimeout(() => navigate(ROUTES.INSPECAO_PRODUTOS_CUFFA_INCLUIR.replace(':uuid', uuid)), 1000);
      }
    } catch (error) {
      console.error("Erro ao salvar dados na API:", error);
      setSnackbar({ open: true, message: 'Erro ao salvar os dados. Tente novamente.', severity: 'error' });
    }
  }, [validate, uuid, possuiProcessados, produtos, salvarDadosParciais, location.pathname, navigate]);

  // Filtrar produtos por tipo
  const produtosAnimais = useMemo(() => produtos.filter(p => p.origem === 'animal' && !p.isCustom), [produtos]);
  const produtosVegetais = useMemo(() => produtos.filter(p => p.origem === 'vegetal' && !p.isCustom), [produtos]);
  const produtosCustomAnimais = useMemo(() => produtos.filter(p => p.origem === 'animal' && p.isCustom), [produtos]);
  const produtosCustomVegetais = useMemo(() => produtos.filter(p => p.origem === 'vegetal' && p.isCustom), [produtos]);

  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Produtos Processados ou Beneficiados" showBackButton />
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography ml={2}>Carregando produtos...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Produtos Processados ou Beneficiados" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Produção de Itens Processados" width="100%">
            <Box mb={4}>
              <Typography fontWeight="bold" gutterBottom>
                A unidade familiar produz itens processados ou beneficiados? *
              </Typography>
              <RadioGroup
                row
                value={possuiProcessados}
                onChange={(e) => {
                  setPossuiProcessados(e.target.value);
                  if (e.target.value !== 'sim') {
                    setProdutos(prev => prev.map(p => ({
                      ...p,
                      selecionado: false,
                      expandido: false
                    })));
                  }
                }}
              >
                <FormControlLabel value="sim" control={<Radio />} label="Sim" />
                <FormControlLabel value="nao" control={<Radio />} label="Não" />
              </RadioGroup>
            </Box>

            {possuiProcessados === 'sim' && (
              <>
                <Typography variant="h6" gutterBottom>
                  Produtos de Origem Animal ({produtosAnimais.length})
                </Typography>

                <Box sx={{
                  maxHeight: '300px',
                  mb: 3,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'auto',
                  p: 1
                }}>
                  {produtosAnimais.map((produto) => (
                    <ProdutoItem
                      key={produto.id}
                      produto={produto}
                      onSelect={handleSelecionarProduto}
                      onExpand={handleExpandirProduto}
                      onChange={debouncedHandleProdutoChange}
                      unidadesMedida={unidadesMedida}
                    />
                  ))}
                </Box>

                {produtosCustomAnimais.map((produto) => (
                  <CustomProdutoItem
                    key={produto.id}
                    produto={produto}
                    onExpand={handleExpandirProduto}
                    onChange={debouncedHandleProdutoChange}
                    onRemove={handleRemoverProdutoCustom}
                    unidadesMedida={unidadesMedida}
                  />
                ))}

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" gutterBottom>
                  Produtos de Origem Vegetal ({produtosVegetais.length})
                </Typography>

                <Box sx={{
                  maxHeight: '300px',
                  mb: 3,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'auto',
                  p: 1
                }}>
                  {produtosVegetais.map((produto) => (
                    <ProdutoItem
                      key={produto.id}
                      produto={produto}
                      onSelect={handleSelecionarProduto}
                      onExpand={handleExpandirProduto}
                      onChange={debouncedHandleProdutoChange}
                      unidadesMedida={unidadesMedida}
                    />
                  ))}
                </Box>

                {produtosCustomVegetais.map((produto) => (
                  <CustomProdutoItem
                    key={produto.id}
                    produto={produto}
                    onExpand={handleExpandirProduto}
                    onChange={debouncedHandleProdutoChange}
                    onRemove={handleRemoverProdutoCustom}
                    unidadesMedida={unidadesMedida}
                  />
                ))}

                <Divider sx={{ my: 4 }} />

                <Box mb={4} sx={{ border: '1px dashed #ccc', p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Adicionar Produto Não Listado
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Nome do produto"
                        value={novoProdutoCustom}
                        onChange={(e) => setNovoProdutoCustom(e.target.value.toLocaleUpperCase())}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Select
                        fullWidth
                        value={origemCustom}
                        onChange={(e) => setOrigemCustom(e.target.value as 'animal' | 'vegetal')}
                      >
                        <MenuItem value="animal">Origem Animal</MenuItem>
                        <MenuItem value="vegetal">Origem Vegetal</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleAdicionarProdutoCustom}
                        disabled={!novoProdutoCustom.trim()}
                        fullWidth
                      >
                        Adicionar
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}

            <Box mt={4} display="flex" justifyContent="center" gap={2}>
              <Button variant="outlined" color="secondary" onClick={() => navigate(ROUTES.DETALHES_PRODUCAO_INCLUIR.replace(':uuid', uuid || ''))}>
                Retornar
              </Button>
              <Button variant="outlined" color="secondary" onClick={() => handleSave(false)}>
                Salvar
              </Button>
              <Button variant="contained" color="primary" onClick={() => handleSave(true)}>
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MenuProvider>
  );
}
